/**
 * Multi-Chat Page
 * Multi-Chat System: Main chat interface with dual sidebars
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Tooltip,
  IconButton,
  Fab,
} from '@mui/material';
import {
  Menu as MenuIcon,
  ListAlt as ListAltIcon,
  DeleteSweep as DeleteSweepIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  ExtensionOutlined as ToolsIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { useSnackbar } from '@/contexts/SnackbarContext';
import { useDialogState } from '@/hooks/ui/useDialogState';

// Chat components
import {
  ChatMessage,
  ChatInput,
  DeleteConfirmDialog,
} from '../components/chat';

// Conversation components
import {
  ConversationSidebar,
  DocumentReferencesSidebar,
} from '../components/conversation';

// API hooks
import {
  useGetConversation,
  useCreateConversation,
  useClearConversationMessages,
  useGenerateConversationTitle,
  useUpdateConversation,
} from '../hooks/api/useConversations';
import { useSendMessage } from '../hooks/api/useConversationChat';

// State management
import { useConversationStore } from '../stores/conversationStore';
import { log } from '../utils/errors/logger';
import { useSystem } from '../hooks/api/config/useSystemOrganization';
import type { ChatSource, ToolStep } from '../api/ApiClient';

/**
 * Helper to get appropriate error message for API errors
 */
type ChatError = {
  status?: number;
  code?: string;
  message?: string;
};

// Draft-map key for a conversation that has not been created yet (the "new
// chat" composer before its first message assigns a real conversation id).
const NEW_CHAT_DRAFT_KEY = '__new_chat__';

const getErrorMessage = (error: unknown, t: TFunction): string => {
  const chatError = (error as ChatError) ?? {};
  const isQuotaError =
    chatError.status === 429 || chatError.code === 'QUOTA_EXCEEDED';
  return isQuotaError ? t('common.errors.quotaExceeded') : t('chat.errorMessage');
};

const MultiChat: React.FC = () => {
  const { t } = useTranslation();
  const { showSuccess, showError } = useSnackbar();
  const dialogState = useDialogState<{ id?: string }>();
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const bottomAnchorRef = useRef<HTMLDivElement>(null);
  const shouldAutoScrollRef = useRef(true);
  const scrollRafRef = useRef<number | null>(null);
  const navigate = useNavigate();
  const { system } = useSystem();

  // Get conversation ID from URL params
  const { conversationId: currentConversationId } = useParams<{ conversationId?: string }>();

  // State management - UI preferences only
  const {
    conversationSidebarOpen,
    documentReferencesSidebarOpen,
    toggleConversationSidebar,
    toggleDocumentReferencesSidebar,
    setConversationSidebarOpen,
  } = useConversationStore();

  // Local state
  // Per-conversation unsent drafts: each chat keeps its OWN typed-but-not-sent
  // input, so switching away and back restores it and a draft never leaks into
  // another chat. Stored in a ref (survives re-renders) keyed by conversation
  // id; a version counter triggers re-render when the active draft changes. A
  // not-yet-created conversation uses the NEW_CHAT_DRAFT_KEY bucket.
  const draftsRef = useRef<Map<string, string>>(new Map());
  const [, setDraftVersion] = useState(0);
  const draftKey = currentConversationId || NEW_CHAT_DRAFT_KEY;
  const draftKeyRef = useRef(draftKey);
  draftKeyRef.current = draftKey;
  const input = draftsRef.current.get(draftKey) ?? '';
  const setInput = useCallback(
    (value: string) => {
      // Read the live key via a ref so this stays identity-stable (the
      // memoized ChatInput depends on it) yet always writes the active chat.
      draftsRef.current.set(draftKeyRef.current, value);
      setDraftVersion((v) => v + 1);
    },
    []
  );
  const [streamingMessage, setStreamingMessage] = useState<string>('');
  const [streamingSources, setStreamingSources] = useState<ChatSource[]>([]);
  const [streamingToolSteps, setStreamingToolSteps] = useState<ToolStep[]>([]);
  const [streamingConversationId, setStreamingConversationId] = useState<string | null>(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const [showScrollToLatest, setShowScrollToLatest] = useState(false);
  // Optimistic local override for the per-conversation tools toggle: flipped
  // immediately on click, cleared once the mutation settles (success patches
  // the query cache, so `currentConversation.tools_enabled` becomes
  // authoritative again; failure reverts it explicitly below).
  const [toolsEnabledOverride, setToolsEnabledOverride] = useState<boolean | null>(null);

  // API hooks
  const { data: currentConversation, isLoading: conversationLoading, error: conversationError } = useGetConversation(currentConversationId || null, {
    enabled: Boolean(currentConversationId),
  });
  const createConversationMutation = useCreateConversation();
  const sendMessageMutation = useSendMessage();
  const clearMessagesMutation = useClearConversationMessages();
  const generateTitleMutation = useGenerateConversationTitle();
  const updateConversationMutation = useUpdateConversation();
  const messageCount = Array.isArray(currentConversation?.messages)
    ? currentConversation.messages.length
    : 0;

  const isNearBottom = (element: HTMLDivElement, thresholdPx = 120): boolean => {
    const distanceFromBottom = element.scrollHeight - element.scrollTop - element.clientHeight;
    return distanceFromBottom <= thresholdPx;
  };

  const scrollToBottomInstant = () => {
    const container = messagesContainerRef.current;
    if (!container) {return;}
    container.scrollTop = container.scrollHeight;
    setShowScrollToLatest(false);
  };

  const scheduleAutoScroll = () => {
    if (!shouldAutoScrollRef.current || scrollRafRef.current !== null) {return;}

    scrollRafRef.current = window.requestAnimationFrame(() => {
      scrollRafRef.current = null;
      scrollToBottomInstant();
    });
  };

  useEffect(() => {
    return () => {
      if (scrollRafRef.current !== null) {
        window.cancelAnimationFrame(scrollRafRef.current);
      }
    };
  }, []);

  useEffect(() => {
    shouldAutoScrollRef.current = true;
  }, [currentConversationId]);

  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) {return;}

    const handleScroll = () => {
      const nearBottom = isNearBottom(container);
      shouldAutoScrollRef.current = nearBottom;
      setShowScrollToLatest(!nearBottom && messageCount > 0);
    };

    handleScroll();
    container.addEventListener('scroll', handleScroll);
    return () => {
      container.removeEventListener('scroll', handleScroll);
    };
  }, [currentConversationId, messageCount]);

  useEffect(() => {
    scheduleAutoScroll();
  }, [currentConversation?.messages]);

  // Ensure streaming chunks scroll after they are rendered in the DOM.
  useEffect(() => {
    if (isStreaming && streamingMessage) {
      scheduleAutoScroll();
    }
  }, [isStreaming, scheduleAutoScroll, streamingMessage]);

  // Handle invalid conversation ID
  useEffect(() => {
    // Conversation ID exists but conversation not found (404 error)
    // This can happen if conversation was deleted on backend
    if (currentConversationId && conversationError && !conversationLoading && !createConversationMutation.isPending) {
      log.warn('Conversation not found, navigating to chat home', {
        invalidConversationId: currentConversationId,
        error: conversationError
      });

      // Navigate to chat home (no conversation ID)
      void navigate('/chat', { replace: true });
      showError(t('conversation.notFound', { defaultValue: 'Conversation not found' }));
    }
  }, [currentConversationId, conversationLoading, conversationError, createConversationMutation.isPending, navigate, showError, t]);

  // Event handlers
  const handleSend = async () => {
    if (!input.trim()) {return;}
    // The input stays editable while a response streams (so the user can compose
    // the next message), but a second send must not fire until this one settles.
    // The Send button is disabled during loading; this guards the Enter-key path.
    if (isLoading) {return;}

    const messageContent = input.trim();
    let conversationIdToUse = currentConversationId;
    
    // If no current conversation, create one first
    if (!conversationIdToUse) {
      try {
        log.info('Creating new conversation for first message');
        const newConversation = await createConversationMutation.mutateAsync(undefined);
        conversationIdToUse = newConversation.id;

        // Navigate to the new conversation
        void navigate(`/chat/${newConversation.id}`, { replace: true });
        // Continue with sending the message after creating conversation
      } catch (error) {
        log.error('Failed to create conversation before sending message', error);
        showError(t('conversation.createFailed'));
        return;
      }
    }

    // Clear the draft for the chat the message was composed in. For a brand-new
    // conversation that was the "__new_chat__" bucket (its id did not exist when
    // the user typed), so clear that explicitly rather than the now-current id.
    draftsRef.current.delete(currentConversationId || NEW_CHAT_DRAFT_KEY);
    setDraftVersion((v) => v + 1);
    setIsStreaming(true);
    setStreamingMessage('');
    setStreamingSources([]);
    setStreamingToolSteps([]);
    setStreamingConversationId(conversationIdToUse ?? null);
    let messageDelivered = false;

    // Sending a user message should always keep latest content visible.
    shouldAutoScrollRef.current = true;
    scheduleAutoScroll();

    // Shared onChunk handler for both the initial send and the create-and-retry
    // path below: content/sources drive the existing streaming display,
    // toolSteps (present whenever a tool_call/tool_result/tool_steps_discarded
    // event updated the turn's accumulated MCP activity) drives the live
    // ToolStepsPanel.
    const handleStreamChunk = (
      chunk: string,
      sources?: ChatSource[],
      sourcesFinal?: boolean,
      toolSteps?: ToolStep[],
      finalContent?: string,
    ) => {
      if (finalContent !== undefined) {
        // Post-processed final answer text replaces the streamed deltas.
        setStreamingMessage(finalContent);
      } else if (chunk) {
        // Update streaming message with new chunk and follow only when user stays near bottom.
        setStreamingMessage(prev => prev + chunk);
        scheduleAutoScroll();
      }
      // Show the source panel only for the FINAL cited-only list. The
      // upfront `context` preview (sourcesFinal=false) is the wide candidate
      // set; displaying it during generation flashes many sources that the
      // answer never cites, which reads as misleading. Defer until final.
      if (sources !== undefined && sourcesFinal) {
        setStreamingSources(sources);
      }
      if (toolSteps !== undefined) {
        setStreamingToolSteps(toolSteps);
        scheduleAutoScroll();
      }
    };

    try {
      log.user.action('send_message', 'MultiChat', {
        conversationId: conversationIdToUse,
        contentLength: messageContent.length
      });

      await sendMessageMutation.mutateAsync({
        conversationId: conversationIdToUse,
        content: messageContent,
        onChunk: handleStreamChunk,
      });
      messageDelivered = true;

      // Auto-generate title if this is the first message
      if (!currentConversation || currentConversation?.message_count === 0) {
        setTimeout(() => {
          void generateTitleMutation.mutate(conversationIdToUse);
        }, 1000); // Delay to ensure message is saved
      }
    } catch (error: unknown) {
      log.error('Failed to send message', error);
      
      // Check if it's a 404 error (conversation doesn't exist)
      const errorMessage = error instanceof Error ? error.message : String(error);
      if (errorMessage.includes('404') || errorMessage.includes('Not Found')) {
        log.warn('Conversation not found during send, creating new conversation', {
          invalidConversationId: currentConversationId
        });

        try {
          // Create new conversation and retry sending
          const newConversation = await createConversationMutation.mutateAsync(undefined);

          // Navigate to the new conversation
          void navigate(`/chat/${newConversation.id}`, { replace: true });

          // Retry sending the message to the new conversation
          await sendMessageMutation.mutateAsync({
            conversationId: newConversation.id,
            content: messageContent,
            onChunk: handleStreamChunk,
          });
          messageDelivered = true;

          showSuccess(t('conversation.created', { title: newConversation.title }));
        } catch (retryError) {
          log.error('Failed to create new conversation and retry message', retryError);
          showError(getErrorMessage(retryError, t));
          setInput(messageContent); // Restore input on error
        }
      } else {
        showError(getErrorMessage(error, t));
        setInput(messageContent); // Restore input on error
      }
    } finally {
      setIsStreaming(false);
      if (!messageDelivered) {
        setStreamingMessage('');
        setStreamingSources([]);
        setStreamingToolSteps([]);
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void handleSend();
    }
  };

  // Stable callbacks for the memoized ChatInput. handleSend/handleKeyDown are
  // re-created every render (handleSend is a large async closure over many
  // deps; wrapping IT in useCallback would be a stale-closure hazard). Instead
  // we keep a ref to the latest handlers and expose identity-stable wrappers,
  // so ChatInput's props never change and its React.memo holds across keystrokes.
  const handlersRef = useRef({ handleSend, handleKeyDown });
  handlersRef.current = { handleSend, handleKeyDown };
  const stableOnSend = useCallback(() => {
    void handlersRef.current.handleSend();
  }, []);
  const stableOnKeyDown = useCallback((e: React.KeyboardEvent) => {
    handlersRef.current.handleKeyDown(e);
  }, []);
  const closeConversationSidebar = useCallback(() => {
    setConversationSidebarOpen(false);
  }, []);
  const closeDocumentReferencesSidebar = useCallback(() => {
    useConversationStore.setState({ documentReferencesSidebarOpen: false });
  }, []);

  const handleClearConversation = async () => {
    if (!currentConversationId) {return;}

    try {
      await clearMessagesMutation.mutateAsync(currentConversationId);
      showSuccess(t('conversation.messagesCleared'));
      dialogState.closeDeleteConfirm();
      
      log.user.action('clear_conversation', 'MultiChat', { conversationId: currentConversationId });
    } catch (error) {
      log.error('Failed to clear conversation', error);
      showError(t('conversation.clearFailed'));
    }
  };

  // Component logging
  useEffect(() => {
    log.component.mount('MultiChat', { currentConversationId });
  }, []);

  // Memoized so the mapped array AND its per-message objects keep a stable
  // identity across re-renders that do not change the conversation (e.g. every
  // keystroke in the chat input). Without this, the .map() rebuilt a new object
  // per message on every render, defeating React.memo on ChatMessage and
  // re-rendering the whole conversation (markdown + source tables) per keypress.
  const messages = React.useMemo(
    () =>
      Array.isArray(currentConversation?.messages)
        ? currentConversation.messages
            .filter((message): message is { id?: string; role: string; content: string; sources?: unknown[] } =>
              typeof (message as { role?: unknown }).role === 'string' &&
              typeof (message as { content?: unknown }).content === 'string'
            )
            .map(message => ({
              ...message,
              sources: Array.isArray(message.sources) ? (message.sources as ChatSource[]) : undefined,
            }))
        : [],
    [currentConversation?.messages]
  );
  const isStreamingForCurrentConversation =
    streamingConversationId !== null && streamingConversationId === currentConversationId;
  const hasPersistedStreamingMessage = Boolean(
    streamingMessage &&
    messages.some((message) => message.role === 'assistant' && message.content === streamingMessage)
  );
  const shouldRenderStreamingMessage =
    isStreamingForCurrentConversation &&
    (Boolean(streamingMessage) || streamingToolSteps.length > 0) &&
    !hasPersistedStreamingMessage;
  const isLoading = conversationLoading || sendMessageMutation.isPending;

  useEffect(() => {
    if (hasPersistedStreamingMessage) {
      setStreamingMessage('');
      setStreamingToolSteps([]);
    }
  }, [hasPersistedStreamingMessage]);

  const toolsEnabled = toolsEnabledOverride ?? currentConversation?.tools_enabled ?? false;

  useEffect(() => {
    // A stale override must not leak from one conversation to the next.
    setToolsEnabledOverride(null);
  }, [currentConversationId]);

  const handleToggleTools = async () => {
    if (!currentConversationId) {return;}

    const next = !toolsEnabled;
    setToolsEnabledOverride(next);

    try {
      await updateConversationMutation.mutateAsync({
        conversationId: currentConversationId,
        updates: { tools_enabled: next },
      });
    } catch (error) {
      log.error('Failed to update tools_enabled', error);
      showError(t('chat.toolsToggle.updateFailed'));
      setToolsEnabledOverride(!next);
      return;
    }
    // The mutation's onSuccess already patched the query cache with the
    // authoritative value -- drop the local override so future renders read
    // straight from `currentConversation.tools_enabled` again.
    setToolsEnabledOverride(null);
  };

  return (
    <Box
      id="multi-chat-container"
      sx={{
        height: 'calc(100vh - 64px)',
        width: '100%',
        display: 'flex',
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      {/* Left: Conversation Sidebar */}
      <Box
        id="conversation-sidebar-container"
        sx={{
          width: conversationSidebarOpen ? { xs: 260, md: 280, lg: 300 } : 0,
          flexShrink: 0,
          transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          overflow: 'hidden',
          borderRight: conversationSidebarOpen ? 1 : 0,
          borderColor: 'divider',
        }}
      >
        <ConversationSidebar
          open={conversationSidebarOpen}
          onClose={closeConversationSidebar}
        />
      </Box>
      
      {/* Center: Main Chat Area */}
      <Box 
        id="main-chat-area"
        component="main" 
        sx={{ 
          flexGrow: 1, 
          display: 'flex', 
          flexDirection: 'column', 
          overflow: 'hidden',
          position: 'relative',
          minWidth: 0, // Prevents flex item from overflowing
        }}
      >
        {/* Messages Area */}
        <Box
          id="chat-messages-area"
          ref={messagesContainerRef}
          role="log"
          aria-live="polite"
          aria-label={t('chat.messagesArea', { defaultValue: 'Chat messages' })}
          sx={{
            flexGrow: 1,
            overflow: 'auto',
            p: { xs: 1, sm: 1.5, md: 2 },
            bgcolor: '#f8f8f8',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Empty State */}
          {messages.length === 0 && !conversationLoading && (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column',
              justifyContent: 'center', 
              alignItems: 'center', 
              height: '100%',
              p: 3 
            }}>
              <Typography 
                variant="h6" 
                sx={{ 
                  color: '#003064',
                  mb: 2,
                  fontWeight: 500
                }} 
                align="center"
              >
                {t('chat.welcome', { systemName: system?.name || 'smarty' })}
              </Typography>
              <Typography 
                variant="body1" 
                sx={{ color: '#666666' }} 
                align="center"
              >
                {t('chat.askAnything')}
              </Typography>
            </Box>
          )}

          {/* Messages */}
              {messages.map((message, index) => (
                <ChatMessage
                  key={message.id || index}
                  message={message}
                  isUser={message.role === 'user'}
                  conversationId={currentConversationId}
                />
              ))}

          {/* Streaming Message */}
          {shouldRenderStreamingMessage && (
            <ChatMessage
              key="streaming-message"
              message={{
                role: 'assistant',
                content: streamingMessage,
                sources: streamingSources,
                toolSteps: streamingToolSteps,
              }}
              isUser={false}
              conversationId={currentConversationId}
            />
          )}

          {/* Loading Indicator */}
          {(isLoading || (isStreaming && isStreamingForCurrentConversation)) && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: 1,
                my: 2,
                minHeight: 32,
              }}
            >
              {(isLoading || (isStreaming && isStreamingForCurrentConversation && !streamingMessage)) && (
                <CircularProgress size={24} sx={{ color: '#003064' }} />
              )}
              <Typography variant="caption" color="text.secondary">
                {t('chat.generating', { defaultValue: 'Generating response...' })}
              </Typography>
            </Box>
          )}

          <div ref={bottomAnchorRef} />
        </Box>

        {showScrollToLatest && (
          <Box
            sx={{
              position: 'absolute',
              right: 24,
              bottom: 126,
              zIndex: 5,
            }}
          >
            <Tooltip title={t('chat.scrollToLatest', { defaultValue: 'Scroll to latest message' })} arrow>
              <Fab
                size="small"
                color="primary"
                onClick={scrollToBottomInstant}
                aria-label={t('chat.scrollToLatest', { defaultValue: 'Scroll to latest message' })}
              >
                <KeyboardArrowDownIcon />
              </Fab>
            </Tooltip>
          </Box>
        )}

        {/* Fixed Bottom Input Area */}
        <Box
          id="chat-input-area"
          sx={{
            borderTop: 1,
            borderColor: 'divider',
            bgcolor: 'background.paper',
            p: 1.5,
          }}
        >
          {/* Action Buttons */}
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              justifyContent: 'space-between',
              mb: 1,
              pb: 1,
              borderBottom: 1,
              borderColor: 'divider',
            }}
          >
            {/* Left side buttons */}
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
              <Tooltip title={t('conversation.conversations')} arrow>
                <IconButton
                  onClick={toggleConversationSidebar}
                  color={conversationSidebarOpen ? 'primary' : 'default'}
                  sx={{
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      transform: 'scale(1.1)',
                      bgcolor: conversationSidebarOpen ? 'primary.lighter' : 'action.hover',
                    },
                  }}
                >
                  <MenuIcon />
                </IconButton>
              </Tooltip>

              {/* TODO: Commented out new chat button - new chats can be created via conversation sidebar */}
              {/* <Tooltip title={t('conversation.new')} arrow>
                <span>
                  <Fab
                    size="small"
                    color="primary"
                    onClick={() => { void handleCreateNewChat(); }}
                    disabled={createConversationMutation.isPending}
                    sx={{
                      boxShadow: 2,
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: 4,
                      },
                      '&:active': {
                        transform: 'translateY(0)',
                      },
                    }}
                  >
                    <AddIcon />
                  </Fab>
                </span>
              </Tooltip> */}

              {/* TODO: Commented out manual title generation button - auto-generation still works on first message */}
              {/* {currentConversationId && messages.length > 0 && (
                <Tooltip title={t('conversation.generateTitle')} arrow>
                  <span>
                    <Fab
                      size="small"
                      color="secondary"
                      onClick={() => generateTitleMutation.mutate(currentConversationId)}
                      disabled={generateTitleMutation.isPending}
                      sx={{
                        boxShadow: 2,
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: 4,
                        },
                        '&:active': {
                          transform: 'translateY(0)',
                        },
                      }}
                    >
                      <AutoFixHighIcon />
                    </Fab>
                  </span>
                </Tooltip>
              )} */}
            </Box>

            {/* Right side buttons */}
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
              {currentConversationId && (
                <Tooltip
                  title={
                    toolsEnabled
                      ? t('chat.toolsToggle.enabledTooltip')
                      : t('chat.toolsToggle.disabledTooltip')
                  }
                  arrow
                >
                  <IconButton
                    onClick={() => { void handleToggleTools(); }}
                    color={toolsEnabled ? 'primary' : 'default'}
                    disabled={updateConversationMutation.isPending}
                    aria-label={
                      toolsEnabled
                        ? t('chat.toolsToggle.ariaLabelEnabled')
                        : t('chat.toolsToggle.ariaLabelDisabled')
                    }
                    aria-pressed={toolsEnabled}
                    sx={{
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        transform: 'scale(1.1)',
                        bgcolor: toolsEnabled ? 'primary.lighter' : 'action.hover',
                      },
                    }}
                  >
                    <ToolsIcon />
                  </IconButton>
                </Tooltip>
              )}

              <Tooltip title={t('conversation.documentReferences')} arrow>
                <IconButton
                  onClick={toggleDocumentReferencesSidebar}
                  color={documentReferencesSidebarOpen ? 'primary' : 'default'}
                  sx={{
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      transform: 'scale(1.1)',
                      bgcolor: documentReferencesSidebarOpen ? 'primary.lighter' : 'action.hover',
                    },
                  }}
                >
                  <ListAltIcon />
                </IconButton>
              </Tooltip>

              {messages.length > 0 && (
                <Tooltip title={t('chat.clearHistory')} arrow>
                  <IconButton
                    onClick={() => dialogState.openDeleteConfirm({ id: currentConversationId })}
                    color="error"
                    disabled={clearMessagesMutation.isPending}
                    sx={{
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        transform: 'scale(1.1)',
                        bgcolor: 'error.lighter',
                      },
                    }}
                  >
                    <DeleteSweepIcon />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </Box>

          {/* Chat Input */}
          <ChatInput
            input={input}
            loading={isLoading}
            onInputChange={setInput}
            onSend={stableOnSend}
            onKeyDown={stableOnKeyDown}
          />
        </Box>
      </Box>

      {/* Right: Document References Sidebar */}
      <Box
        id="document-references-sidebar-container"
        sx={{
          width: documentReferencesSidebarOpen ? { xs: 280, md: 300, lg: 320 } : 0,
          flexShrink: 0,
          transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          overflow: 'hidden',
          borderLeft: documentReferencesSidebarOpen ? 1 : 0,
          borderColor: 'divider',
        }}
      >
        <DocumentReferencesSidebar
          open={documentReferencesSidebarOpen}
          onClose={closeDocumentReferencesSidebar}
        />
      </Box>
      
      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        open={dialogState.deleteConfirmOpen}
        loading={clearMessagesMutation.isPending}
        onClose={dialogState.closeDeleteConfirm}
        onConfirm={() => { void handleClearConversation(); }}
        title={t('chat.deleteConfirm.title')}
        message={t('chat.deleteConfirm.message')}
        confirmLabel={t('chat.deleteConfirm.confirm')}
        cancelLabel={t('chat.deleteConfirm.cancel')}
        showDeleteIcon
      />
    </Box>
  );
};

export default MultiChat;
